import {Button, Form, Input, InputNumber, Select} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import styles from './mobileSetting.module.scss';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {useState} from 'react';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {UPDATE_APP_CONFIGURATION} from 'graphql/queries/mobileSettings';
import {useMutation} from '@apollo/client';
import {toast} from 'react-toastify';
import {isDabaEmail} from 'utils/helper';

// WalletCurrency enum — drives the dropdown so we never send a free-text code.
const WALLET_CURRENCIES = ['USD', 'EUR', 'GBP', 'NGN', 'XOF', 'XAF', 'CAD'];

export const InventoryPredictionSettings = ({data, refetch}) => {
  const [openModal, setOpenModal] = useState(false);
  const [formValues, setFormValues] = useState({});

  const [updateAppConfig, {loading}] = useMutation(UPDATE_APP_CONFIGURATION, {
    errorPolicy: 'all',
  });

  // The API replaces the threshold array wholesale, so the editor always loads
  // and saves the complete list.
  const initialThresholds = (data?.highPriorityThresholds || []).map(
    ({currencyCode, amount}) => ({currencyCode, amount}),
  );

  return (
    <div className={styles.body}>
      <div>
        <h2>Inventory Prediction Settings</h2>
      </div>
      <Form
        onFinish={values => {
          setFormValues(values);
          setOpenModal(true);
        }}
        layout="vertical">
        <Form.Item
          tooltip="Total order value, per currency, that flags a run as high-priority. A currency with no entry can never flag high priority."
          label="High Priority Thresholds">
          <Form.List
            name="highPriorityThresholds"
            initialValue={initialThresholds}>
            {(fields, {add, remove}) => (
              <div>
                {fields.map(({key, name, ...rest}) => (
                  <div key={key} className={styles.thresholdRow}>
                    <Form.Item
                      {...rest}
                      name={[name, 'currencyCode']}
                      rules={[{required: true, message: 'Select a currency'}]}
                      style={{flex: 1, marginBottom: 8}}>
                      <Select
                        placeholder="Currency"
                        options={WALLET_CURRENCIES.map(code => ({
                          label: code,
                          value: code,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, 'amount']}
                      rules={[{required: true, message: 'Enter an amount'}]}
                      style={{flex: 2, marginBottom: 8}}>
                      <InputNumber
                        min={0}
                        placeholder="5,000,000"
                        className="input-number"
                        style={{width: '100%'}}
                        formatter={value =>
                          `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                        }
                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                      />
                    </Form.Item>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      onClick={() => remove(name)}
                    />
                  </div>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => add({currencyCode: undefined, amount: null})}>
                  Add Currency
                </Button>
              </div>
            )}
          </Form.List>
        </Form.Item>
        <Form.Item
          className={styles.timezone}
          name="financeTeamEmail"
          tooltip="Default recipient for the prediction report email. Manual runs can override this per-run."
          initialValue={data?.financeTeamEmail}
          rules={[
            {required: true, message: 'This is a required field'},
            {
              validator: (_, value) => {
                if (!value || isDabaEmail(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('Use an official daba email address'),
                );
              },
            },
          ]}
          label="Finance Team Email">
          <Input placeholder="finance@dabafinance.com" />
        </Form.Item>
        <SubmitButton label="Save Changes" />
      </Form>
      <ConfirmModal
        openModal={openModal}
        setOpenModal={() => setOpenModal(false)}
        loading={loading}
        title="Update Inventory Prediction Settings"
        confirmText="The thresholds you see here replace the saved list in full — any currency not listed will be removed. Are you sure you want to update these settings?"
        handleOperation={() => {
          const thresholds = (formValues.highPriorityThresholds || [])
            .filter(entry => entry?.currencyCode && entry?.amount != null)
            .map(entry => ({
              currencyCode: entry.currencyCode,
              amount: Number(entry.amount),
            }));
          const duplicates = thresholds
            .map(entry => entry.currencyCode)
            .filter((code, index, all) => all.indexOf(code) !== index);
          if (duplicates.length) {
            toast.error(
              `One threshold per currency — duplicated: ${duplicates
                .filter((code, index) => duplicates.indexOf(code) === index)
                .join(', ')}`,
            );
            return;
          }
          updateAppConfig({
            variables: {
              data: {
                inventoryPredictionSettings: {
                  highPriorityThresholds: thresholds,
                  ...(formValues.financeTeamEmail && {
                    financeTeamEmail: formValues.financeTeamEmail.trim(),
                  }),
                },
              },
            },
          })
            .then(({data: {updateAppConfig}}) => {
              if (updateAppConfig.__typename === 'Error') {
                toast.error(updateAppConfig.message);
              } else {
                toast.success('Updated Successfully');
                refetch();
                setOpenModal(false);
              }
            })
            .catch(error => toast.error(error));
        }}
      />
    </div>
  );
};
